import React, { useState } from "react";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import useMapDropdownStyles from "../hooks/useMapDropdownStyles";

const MapLayersLayer = ({ layer }) => {
    const [expanded, setExpanded] = useState(false);
    const disabled = layer.sublayers.length > 0 && layer.sublayers.every((s) => s.disabled === true);

    const handleLayerClick = (e, handleClick) => {
        e.stopPropagation();
        handleClick();
    };

    return (
        <Box>
            <Tooltip title={disabled ? "This layer temporarily unavailable." : ""} placement="top">
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                    <Checkbox
                        checked={layer.checked}
                        onClick={(e) => handleLayerClick(e, layer.handleLayerClick)}
                        indeterminate={layer.indeterminate}
                        disabled={disabled}
                    />

                    <Typography variant="body1" sx={{ mr: "auto" }} color={disabled ? "textDisabled" : "inherit"}>
                        {layer.label}
                    </Typography>

                    {layer.sublayers.length > 0 && disabled === false ? (
                        <IconButton onClick={() => setExpanded((prev) => !prev)}>{expanded === false ? <ExpandMoreIcon /> : <ExpandLessIcon />}</IconButton>
                    ) : (
                        <></>
                    )}
                </Box>
            </Tooltip>
            {layer.sublayers.length > 0 && disabled === false ? (
                <Collapse in={expanded}>
                    <Box sx={{ display: "flex", flexDirection: "column", px: 2 }}>
                        {layer.sublayers.map((sublayer, i) => (
                            <Tooltip key={i} title={sublayer.disabled ? "This layer temporarily unavailable." : ""} placement="top">
                                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                                    {sublayer.handleSublayerClick ? (
                                        <Checkbox
                                            checked={sublayer.checked}
                                            onClick={sublayer.handleSublayerClick}
                                            disabled={sublayer.disabled === true ? true : false}
                                        />
                                    ) : (
                                        <></>
                                    )}
                                    <Typography variant="body1" sx={{ mr: "auto" }} color={sublayer.disabled ? "textDisabled" : "inherit"}>
                                        {sublayer.label}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        ))}
                    </Box>
                </Collapse>
            ) : (
                <></>
            )}
        </Box>
    );
};

export const MapLayersDropdown = ({
    mapLayersOpen,
    setMapLayersOpen,
    mapLayersAnchorEl,
    altitudeLayerRange,
    handleAltitudeLayerRangeChanged,
    sections,
    mapContainer,
    narChecked,
    handleNarTrackSettingChange
}) => {
    const mapDropdownStyles = useMapDropdownStyles(mapLayersAnchorEl);

    const marks = [
        { value: 0, label: "0" },
        { value: 5000, label: "5k" },
        { value: 10000, label: "10k" },
        { value: 15000, label: "15k" },
        { value: 20000, label: "20k" },
        { value: 25000, label: "25k" }
    ];
    return (
        <Popover
            open={mapLayersOpen}
            anchorEl={mapLayersAnchorEl}
            onClose={() => setMapLayersOpen(false)}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "center"
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "center"
            }}
            container={mapContainer ? mapContainer : undefined}
            slotProps={{ paper: { sx: { ...mapDropdownStyles } } }}
        >
            <Box sx={{ p: 2, width: "300px" }}>
                <Typography variant="h5" gutterBottom>
                    Layers
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {altitudeLayerRange && handleAltitudeLayerRangeChanged ? (
                        <Box>
                            <Typography variant="body1" gutterBottom>
                                Altitude (ft)
                            </Typography>

                            <Box sx={{ px: 2 }}>
                                <Slider
                                    value={altitudeLayerRange}
                                    onChange={handleAltitudeLayerRangeChanged}
                                    valueLabelDisplay="auto"
                                    marks={marks}
                                    max={25000}
                                    step={250}
                                    min={0}
                                />
                            </Box>

                            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1, mt: 1 }}>
                                <Checkbox checked={narChecked} onClick={handleNarTrackSettingChange} />

                                <Typography variant="body1" sx={{ mr: "auto" }}>
                                    Show NAR Tracks
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <></>
                    )}
                    {sections.map((section, sectionIdx) => (
                        <Box key={sectionIdx}>
                            <Typography variant="body1">{section.title}</Typography>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {section.layers
                                    .filter(({ visibility }) => visibility === undefined || visibility)
                                    .map((layer, layerIdx) => (
                                        <MapLayersLayer key={layerIdx} layer={layer} />
                                    ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Popover>
    );
};
